import { useRef, useState } from 'react';
import type { AttachmentEntityType } from '@accounting/shared';
import { MAX_ATTACHMENT_BYTES } from '@accounting/shared';
import {
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
} from '../../hooks/useAttachments';
import { ApiError, downloadFile } from '../../lib/api';

interface AttachmentsPanelProps {
  companyId: string | null;
  entityType: AttachmentEntityType;
  entityId: string;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Read a File as base64 (without the data: URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export function AttachmentsPanel({ companyId, entityType, entityId }: AttachmentsPanelProps) {
  const { data: attachments = [], isLoading } = useAttachments(companyId, entityType, entityId);
  const upload = useUploadAttachment(companyId, entityType, entityId);
  const remove = useDeleteAttachment(companyId, entityType, entityId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const uploadFile = async (file: File) => {
    setLocalError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setLocalError(`"${file.name}" is too large (max ${formatBytes(MAX_ATTACHMENT_BYTES)}).`);
      return;
    }
    try {
      const content = await fileToBase64(file);
      upload.mutate({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        content,
      });
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Could not read file');
    }
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-selecting the same file
    for (const file of files) await uploadFile(file);
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!entityId) return;
    const files = Array.from(e.dataTransfer.files ?? []);
    for (const file of files) await uploadFile(file);
  };

  const download = (id: string, fileName: string) => {
    void downloadFile(`/companies/${companyId}/attachments/${id}/download`, undefined, fileName);
  };

  const uploadError =
    upload.error instanceof ApiError
      ? (upload.error.body as { error?: string } | null)?.error ?? 'Upload failed'
      : upload.error?.message;

  return (
    <div
      className="card"
      style={{
        padding: 16,
        maxWidth: 760,
        outline: dragging ? '2px dashed var(--primary)' : undefined,
        outlineOffset: dragging ? '-6px' : undefined,
        background: dragging ? 'var(--bg-page)' : undefined,
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (entityId) setDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragging(false);
      }}
      onDrop={onDrop}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 15, margin: 0 }}>Attachments</h2>
        <button
          className="btn-secondary"
          onClick={() => fileInput.current?.click()}
          disabled={upload.isPending || !entityId}
        >
          {upload.isPending ? 'Uploading…' : '+ Attach file'}
        </button>
        <input ref={fileInput} type="file" multiple style={{ display: 'none' }} onChange={onPick} />
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        {dragging
          ? 'Drop files to upload…'
          : `Drag & drop files here, or click “Attach file.” Receipts, invoices, or supporting documents (max ${formatBytes(MAX_ATTACHMENT_BYTES)} each).`}
      </p>

      {(localError || uploadError) && (
        <div className="field-error" style={{ marginBottom: 8 }}>{localError ?? uploadError}</div>
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && attachments.length === 0 && (
        <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>No attachments yet.</p>
      )}

      {attachments.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>File</th>
              <th style={{ textAlign: 'right' }}>Size</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {attachments.map((a) => (
              <tr key={a.id}>
                <td>
                  <button className="amount-link" onClick={() => download(a.id, a.fileName)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>
                    {a.fileName}
                  </button>
                  <div className="muted" style={{ fontSize: 11 }}>{a.mimeType}</div>
                </td>
                <td style={{ textAlign: 'right' }} className="muted">{formatBytes(a.sizeBytes)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button
                    className="btn-danger"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (confirm(`Remove "${a.fileName}"?`)) remove.mutate(a.id);
                    }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
